import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@arcadeai/design-system/components/ui/atoms/alert";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tabs";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { AlertCircle, ExternalLink } from "lucide-react";
import type { FieldProps } from "@/app/servers/types";

interface TypeFieldProps extends FieldProps {
  onTypeChange?: (type: "http" | "mcp") => void;
}

export const TypeField = ({
  form,
  canUpdate,
  onTypeChange,
}: TypeFieldProps) => (
  <FormField
    control={form.control}
    name="type"
    render={({ field }) => (
      <FormItem>
        <FormLabel>Server Type</FormLabel>
        <Alert className="mt-1 border-blue-200 bg-blue-50/50 dark:border-blue-500/30 dark:bg-blue-950/20">
          <AlertTitle className="mb-2 flex items-center gap-2 font-semibold text-foreground text-sm">
            <AlertCircle className="size-4 shrink-0 text-blue-600 dark:text-blue-400" />
            Choose a server type
          </AlertTitle>
          <AlertDescription className="space-y-2.5 text-sm">
            <p>
              <span className="font-semibold text-blue-700 dark:text-blue-400">
                Remote MCP:{" "}
              </span>
              <span className="text-muted-foreground">
                For external Model Context Protocol servers.
              </span>
            </p>

            <p>
              <span className="font-semibold text-blue-700 dark:text-blue-400">
                Arcade:{" "}
              </span>
              <span className="text-muted-foreground">
                For self-hosted Arcade servers you created with{" "}
                <a
                  className="text-link hover:underline"
                  href="https://docs.arcade.dev/en/home/build-tools/create-a-mcp-server"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <code className="inline-flex items-center gap-1 rounded bg-blue-100 px-1 py-0.5 font-medium font-mono text-blue-700 text-xs dark:bg-blue-900/50 dark:text-blue-300">
                    arcade-mcp
                    <ExternalLink className="size-3 shrink-0" />
                  </code>
                </a>{" "}
                or deployed with{" "}
                <a
                  className="text-link hover:underline"
                  href="https://docs.arcade.dev/home/serve-tools/arcade-deploy"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <code className="inline-flex items-center gap-1 rounded bg-blue-100 px-1 py-0.5 font-medium font-mono text-blue-700 text-xs dark:bg-blue-900/50 dark:text-blue-300">
                    arcade deploy
                    <ExternalLink className="size-3 shrink-0" />
                  </code>
                </a>
                .
              </span>
            </p>
          </AlertDescription>
        </Alert>
        <Tabs
          onValueChange={(value) => {
            // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
            field.onChange(value as "http" | "mcp");
            // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
            onTypeChange?.(value as "http" | "mcp");
          }}
          value={field.value}
        >
          <FormControl>
            <TabsList className="mt-2 w-full gap-1">
              <TabsTrigger
                className="flex-1 hover:border-primary/70 dark:data-active:border-primary"
                disabled={!canUpdate}
                value="mcp"
              >
                Remote MCP
              </TabsTrigger>
              <TabsTrigger
                className="flex-1 hover:border-primary/70 dark:data-active:border-primary"
                disabled={!canUpdate}
                value="http"
              >
                Arcade
              </TabsTrigger>
            </TabsList>
          </FormControl>
        </Tabs>
        <FormMessage />
      </FormItem>
    )}
  />
);
